import React, { useState } from 'react';
import arrowDown from '../../assets/images/dropdown_down_grey.png';
import { Link, useLocation } from 'react-router-dom';
import { useThemeContext } from '../../contexts/ThemeContext';
import { useSidebarContext } from '../../contexts/SidebarContext';
import { useAIContext } from '../AI/AIContext';
import ThemeToggle from '../utils/ThemeToggle';
import NavLinks from './NavLinks';
import userAvatarPlacehoder from '../../assets/images/profile_placeholder.png';
import settingIcon from '../../assets/images/settings.png';
import crown from '../../assets/images/crown.png';
import android from '../../assets/images/android-logo.png';
import bot from '../../assets/images/bot.png';
import darkBot from '../../assets/images-night/bot.png';

type CollapsibleSectionProps = {
  children: React.ReactNode;
  defaultExpanded?: boolean;
};

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ 
  children, 
  defaultExpanded = true 
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="space-y-2">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-2 rounded-t-lg hover:bg-grey_light rounded bg-bg_grey_light  transition-colors duration-200"
        aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
      >
        <span className="text-grey_dark">Settings & Profile</span>
        <span className={`transform transition-transform duration-200 ${isExpanded ? '' : 'rotate-180'}`}>
          <img src={arrowDown} alt="Toggle section" className="w-3 h-3" />
        </span>
      </button>
      
      <div className={`transition-all duration-300 overflow-hidden ${isExpanded ? 'max-h-96' : 'max-h-0'}`}>
        <div className="space-y-2">
          {children}
        </div>
      </div>
    </div>
  );
};

const Sidebar: React.FC = () => {
  const { isDark } = useThemeContext();
  const { isSidebarOpen, closeSidebar } = useSidebarContext();
  const { openChat } = useAIContext();
  const location = useLocation();

  // Close sidebar when clicking on a link on mobile
  const handleLinkClick = () => {
    if (window.innerWidth < 768) { // md breakpoint
      closeSidebar();
    }
  };

  const handleAIClick = () => {
    handleLinkClick();
    openChat();
  };

  return (
    <>
      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-gray-700 bg-opacity-50 z-40 md:hidden"
          onClick={closeSidebar}
        />
      )}
      
      <aside 
        className={`text-dark transition-transform duration-300 ease-in-out fixed top-0 left-0 h-full z-50
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 md:relative md:z-auto`}
        style={{
          width: '18rem',
          backgroundColor: 'var(--light)',
          overflowY: 'auto',
        }}
      >
        <div className="bg-light rounded-lg px-6 md:py-4 py-8 flex flex-col justify-between md:h-fill-from-navbar h-full">
          {/* Close button for mobile */}
          <button
            onClick={closeSidebar}
            className="md:hidden absolute right-1 top-1 p-2 rounded-md hover:bg-grey_light transition-colors duration-200"
            aria-label="Close sidebar"
          >
            <div className="w-4 h-4 relative">
              <span className="absolute top-1/2 left-0 w-full h-0.5 bg-grey_dark rounded transform -rotate-45"></span>
              <span className="absolute top-1/2 left-0 w-full h-0.5 bg-grey_dark rounded transform rotate-45"></span>
            </div>
          </button>
          
          {/* Main Navigation Links - Now interchangeable */}
          <NavLinks />

          {/* Collapsible bottom section */}
          <CollapsibleSection defaultExpanded={true}>
            <ThemeToggle />

            <hr className="my-4 border-grey_light" />

                                    <button
              onClick={handleAIClick}
              className="ps-4 py-2 rounded-lg hover:bg-grey_light transition-colors duration-200 justify-center flex items-center w-full"
            >
              <h3 className="w-full text-left">AI Assitant</h3>
              <img
                className="mx-2 rounded-full object-cover"
                src={isDark ? darkBot : bot}
                alt="bot icon"
                style={{
                  width: '24px',
                  height: '24px'
                }}
              />
            </button>

            <button
              onClick={() => {
                handleLinkClick();
                const baseUrl = process.env.REACT_APP_API_URL || '';
                const link = document.createElement('a');
                link.href = `${baseUrl}download/apk`;
                link.setAttribute('download', '');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
              className="ps-4 py-2 rounded-lg hover:bg-grey_light transition-colors duration-200 justify-center flex items-center w-full"
            >
              <h3 className="w-full text-left">Download APK</h3>
              <img
                className="mx-2 rounded-full object-cover"
                src={android}
                alt="android icon"
                style={{
                  width: '20px',
                  height: '20px'
                }}
              />
            </button>

            <Link
              to="/subscription"
              onClick={handleLinkClick}
              className="ps-4 py-2 rounded-lg hover:bg-grey_light transition-colors duration-200 justify-center flex items-center"
            >
              <h3 className="w-full">Upgrade</h3>
              <img
                className="mx-2 rounded-full object-cover"
                src={crown}
                alt="up icon"
                style={{
                  width: '20px',
                  height: '20px'
                }}
              />
            </Link>

            <Link
              to="/profile"
              onClick={handleLinkClick}
              className="ps-4 py-2 rounded-lg hover:bg-grey_light transition-colors duration-200 justify-center flex items-center"
            >
              <h3 className="w-full">Settings</h3>
              <img
                className="mx-2 rounded-full object-cover"
                src={settingIcon}
                alt="settings icon"
                style={{
                  width: '20px',
                  height: '20px'
                }}
              />
            </Link>

  
          </CollapsibleSection>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;